// --------------------------------------------------------------
// Created On: 2025-04-18
// Author: Hannah Vaughan
//
// Last Modified: 2025-04-29
// Modified By: Hannah Vaughan
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import styles from "./DeviceTable.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FilteredTableContainer from "src/components/FilteredTableContainer/FilteredTableContainer";
import SortedTableHeader from "src/components/SortedTableHeader/SortedTableHeader";
import { setCurrentUser } from "src/redux/actions";
import { getCurrentUser } from "src/redux/selectors";
import formatDashTimeString from "src/utilities/time/formatDashTimeString";
import formatDateLocal from "src/utilities/time/formatDateLocal";

// Table to display all device search results in the Super Admin Device Search Page.
export default function DeviceTable(props: Props): Component {
  const currentUser = useSelector(getCurrentUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Gets the device type's name from the provided model.
  function formatDeviceType(model: string): string {
    return props.deviceTypes.find((deviceType) => deviceType.model === model)?.name || "";
  }

  // Formats the provided date in mm/dd/yyyy local time format.
  function formatDate(date: string | null): string {
    if (date !== null && date.trim() !== "") {
      let dateString = formatDateLocal(date);
      const newDate = new Date(dateString);
      dateString = newDate.toISOString().split("T")[0];
      return formatDashTimeString(dateString);
    } else {
      return "";
    }
  }

  // Enters the company that the asset or device belongs to, then navigates to the asset if it exists,
  // and the company's monitoring device subscriptions page.
  async function navigateToAssetOrDevice(
    companyId: number,
    companyName: string,
    isPackager: boolean,
    assetId: number
  ): Promise<void> {
    dispatch(
      setCurrentUser(
        currentUser.userId,
        currentUser.userName,
        currentUser.emailAddress,
        currentUser.originalCompanyId,
        companyId,
        companyName,
        false,
        currentUser.permissions,
        isPackager
      )
    );

    if (assetId !== null && assetId !== undefined) {
      navigate(`/asset/${assetId}/data`);
    } else {
      navigate("/monitoring-devices/subscriptions");
    }
  }

  return (
    <div className={`${styles.table} mb-0 pb-0`}>
      <FilteredTableContainer
        title={`Devices (${props.devices.length})`}
        filterPrompt="Filter devices..."
        filter=""
        hasContent={props.devices.length > 0}
        hasFilteredContent={false}
        showFilterInput={false}
        pluralContentType="devices"
        onChangeFilter={() => {
          /* Do nothing. */
        }}
      >
        <table className="table" data-test="device-search-table">
          <thead className={styles.header}>
            <tr>
              <SortedTableHeader
                name="Device Identifier"
                index={1}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
              />
              <SortedTableHeader
                name="Device Type"
                index={2}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
              />
              <SortedTableHeader
                name="Company"
                index={3}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
              />
              <SortedTableHeader
                name="Asset"
                index={4}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
              />
              <SortedTableHeader
                name="Activation Status"
                index={5}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
              />
              <SortedTableHeader
                name="Start Date"
                index={6}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
              />
              <SortedTableHeader
                name="End Date"
                index={7}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
              />
            </tr>
          </thead>
          <tbody>
            {props.devices.map((device) => (
              <tr
                key={`${device.deviceIdentifier}-${device.companyId}`}
                className="selectable-container"
                onClick={() =>
                  navigateToAssetOrDevice(device.companyId, device.companyName, device.isPackager, device.assetId)
                }
              >
                <td>{device.deviceIdentifier}</td>
                <td>{formatDeviceType(device.deviceType)}</td>
                <td>{device.companyName}</td>
                <td>{device.assetId !== null ? device.assetName : "N/A"}</td>
                <td>{device.activationStatus}</td>
                <td>{formatDate(device.subscriptionStartDate)}</td>
                <td>{formatDate(device.subscriptionExpiry)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </FilteredTableContainer>
    </div>
  );
}

interface Props {
  devices: Device[];
  deviceTypes: DeviceType[];
  sortColumnIndex: number;
  sortAscending: boolean;
  onUpdateSort: (newIndex: number) => void;
}

interface DeviceType {
  model: string;
  name: string;
}

interface Device {
  deviceType: string;
  deviceIdentifier: number;
  assetId: number;
  assetName: string;
  companyId: number;
  companyName: string;
  isPackager: boolean;
  subscriptionExpiry: string;
  subscriptionStartDate: string;
  activationStatus: string;
}
